import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  DescriptionTable,
  Forbidden,
  NotFound,
  Notification,
  Page,
  Tab,
  TabContent,
  Tabs,
  TabsHeader,
} from 'shared/components';
import { displayValue, hasPermission } from 'shared/helpers';
import { fetchLicenseUserGroup } from 'licenseUsers/actions';
import LicenseUserGroupUsersTab from './LicenseUserGroupUsersTab';
import LicenseUserGroupLicensesTab from './LicenseUserGroupLicensesTab';
import LicenseUserGroupManagersTab from './LicenseUserGroupManagersTab';
import EditLicenseUserGroupModal from './EditLicenseUserGroupModal';
import './LicenseUserGroupDetails.scss';

const getMetadataDisplay = (metadata) => {
  if (!metadata) {
    return '{}';
  }

  if (typeof metadata === 'string') {
    return metadata;
  }

  if (typeof metadata === 'object' && !Object.keys(metadata).length) {
    return '{}';
  }

  try {
    return JSON.stringify(metadata);
  } catch {
    return '{}';
  }
};

const renderCustomerAccount = (
  group,
  customerAccounts = [],
  fallback = '-',
) => {
  const customerAccount = get(group, 'customer_account');

  if (!customerAccount) {
    return fallback;
  }

  if (typeof customerAccount === 'object') {
    const accountId = get(customerAccount, 'id');
    const accountName =
      get(customerAccount, 'name') ||
      get(customerAccount, 'code') ||
      get(customerAccount, 'label');

    if (!accountId) {
      return displayValue(accountName, fallback);
    }

    return (
      <NavLink to={`/customer-accounts/${accountId}`}>
        {displayValue(accountName, fallback)}
      </NavLink>
    );
  }

  const accountFromStore = customerAccounts.find(
    (account) => String(get(account, 'id')) === String(customerAccount),
  );

  if (!accountFromStore) {
    return displayValue(customerAccount, fallback);
  }

  const accountId = get(accountFromStore, 'id');
  const accountName =
    get(accountFromStore, 'name') ||
    get(accountFromStore, 'code') ||
    get(accountFromStore, 'label');

  return (
    <NavLink to={`/customer-accounts/${accountId}`}>
      {displayValue(accountName, fallback)}
    </NavLink>
  );
};

const LicenseUserGroupDetails = () => {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const permissionsList = useSelector((state) =>
    get(state, 'user.details.permissions'),
  );
  const customerAccounts = useSelector((state) =>
    get(state, 'account.customerAccounts', []),
  );
  const canManageLicenseUsers = hasPermission(
    permissionsList,
    'manage_license_users',
  );
  const canManageUserGroups = hasPermission(
    permissionsList,
    'manage_user_groups',
  );

  const [group, setGroup] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [isEditModalDisplayed, setEditModalDisplay] = useState(false);

  useEffect(() => {
    if (!canManageLicenseUsers) {
      return;
    }

    let isMounted = true;
    setLoading(true);
    setNotFound(false);
    setHasError(false);

    fetchLicenseUserGroup(groupId)
      .then((res) => {
        if (!isMounted) return;
        setGroup(get(res, 'data', null));
        setLoading(false);
      })
      .catch((error) => {
        if (!isMounted) return;

        const status = get(error, 'response.status');
        if (status === 404) {
          setNotFound(true);
        } else {
          setHasError(true);
          Notification(
            'error',
            t('Error occured'),
            t('There was an error while getting user group details'),
          );
        }

        setGroup(null);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [canManageLicenseUsers, groupId, retryToken, t]);

  const handleRetry = () => setRetryToken((token) => token + 1);
  const handleEditSuccess = () => {
    setEditModalDisplay(false);
    setRetryToken((token) => token + 1);
  };

  if (!canManageLicenseUsers) {
    return (
      <Page>
        <Forbidden
          text={t('Missing permission')}
          description={t('You do not have permission to perform this action.')}
        />
      </Page>
    );
  }

  const breadcrumb = (
    <span>
      <NavLink to="/license_users">{t('License users')}</NavLink>
      {' / '}
      <NavLink to="/license_users/user_groups">{t('User groups')}</NavLink>
    </span>
  );

  if (isLoading && !group) {
    return (
      <Page title={t('License user group')} breadcrumb={breadcrumb}>
        <ContentLoader text={t('Getting user group details')} />
      </Page>
    );
  }

  if ((notFound || hasError) && !group) {
    return (
      <Page title={t('License user group')} breadcrumb={breadcrumb}>
        {notFound ? (
          <NotFound />
        ) : (
          <div className="LicenseUserGroupDetails-error">
            <p>{t('User group details could not be loaded.')}</p>
            <Button theme="info" size="md" onClick={handleRetry}>
              {t('Retry')}
            </Button>
          </div>
        )}
      </Page>
    );
  }

  return (
    <Page
      title={displayValue(get(group, 'name'), t('License user group'))}
      breadcrumb={breadcrumb}
    >
      <div className="LicenseUserGroupDetails">
        <div className="LicenseUserGroupDetails-actions">
          {canManageUserGroups && (
            <Button
              theme="info"
              size="md"
              onClick={() => setEditModalDisplay(true)}
            >
              {t('Edit user group')}
            </Button>
          )}
        </div>

        <DescriptionTable
          details={[
            {
              label: t('Name'),
              value: displayValue(get(group, 'name')),
            },
            {
              label: t('Code'),
              value: displayValue(get(group, 'code')),
            },
            {
              label: t('Description'),
              value: displayValue(get(group, 'description')),
            },
            {
              label: t('Customer account'),
              value: renderCustomerAccount(group, customerAccounts, t('N/A')),
            },
            {
              label: t('Metadata'),
              value: (
                <span className="LicenseUserGroupDetails-metadata">
                  {getMetadataDisplay(get(group, 'metadata'))}
                </span>
              ),
            },
          ]}
        />

        <div className="LicenseUserGroupDetails-tabs">
          <Tabs>
            <TabsHeader>
              <Tab>{t('Users')}</Tab>
              <Tab>{t('Licenses')}</Tab>
              <Tab>{t('License managers')}</Tab>
            </TabsHeader>

            <TabContent>
              <LicenseUserGroupUsersTab />
            </TabContent>

            <TabContent>
              <LicenseUserGroupLicensesTab />
            </TabContent>

            <TabContent>
              <LicenseUserGroupManagersTab />
            </TabContent>
          </Tabs>
        </div>

        {canManageUserGroups && isEditModalDisplayed && group && (
          <EditLicenseUserGroupModal
            group={group}
            groupID={groupId}
            closeCb={() => setEditModalDisplay(false)}
            confirmCb={handleEditSuccess}
          />
        )}
      </div>
    </Page>
  );
};

export default LicenseUserGroupDetails;
